import {Routes} from '@angular/router';
import {LeavingPageGuardService} from 'app/guards/leaving-page-guard.service';
import {CheckInternetConnectionComponent} from './components/check-internet-connection/check-internet-connection.component';
import {InternalServerErrorComponent} from './components/internal-server-error/internal-server-error.component';
import {PageNotFoundComponent} from './components/page-not-found/page-not-found.component';
import {SignOutComponent} from './components/sign-out/sign-out.component';
import {AppUrls} from './core/url/app-urls.service';
import {AuthGuard} from './guards/auth.guard';
import {NoAuthGuard} from './guards/no-auth.guard';
import {PermissionsGuard, PermissionsGuardData} from './guards/permissions.guard';
import {LayoutSwitcherService} from 'app/features/analytics/guards/layout-switcher.service';
import {HotjarService} from 'app/core/services/hotjar.service';
import {AutoDemoSignInGuard} from 'app/guards/auto-demo-sign-in.guard';
import {BeforeSignInGuard} from 'app/features/sign-in/services/before-sign-in.guard';
import {DefaultMainPageComponent} from 'app/components/default-main-page/default-main-page.component';
import {UaSettingsService} from 'app/features/analytics/service/ua-settings.service';
import {OnboardingGuard} from 'app/features/onboarding/guards/onboarding.guard';
import {AppNewReleaseResolver} from 'app/features/apps/resolvers/app-new-release.resolver';
import {PreviewPageComponent} from 'app/features/notifications/preview-page/preview-page.component';

export const routes: Routes = [
    {
        path: AppUrls.SIGN_IN,
        canActivate: [AutoDemoSignInGuard, HotjarService, BeforeSignInGuard],
        canDeactivate: [HotjarService],
        resolve: {layout: LayoutSwitcherService},
        loadChildren: () => import('./features/sign-in/sign-in.module').then(m => m.SignInModule),
    },
    {
        path: AppUrls.SIGN_UP,
        canActivate: [NoAuthGuard, HotjarService],
        canDeactivate: [HotjarService],
        resolve: {layout: LayoutSwitcherService},
        loadChildren: () => import('./features/sign-up/sign-up.module').then(m => m.SignUpModule),
    },
    {
        path: AppUrls.REMIND_PASSWORD,
        canActivate: [NoAuthGuard, HotjarService],
        canDeactivate: [HotjarService],
        resolve: {layout: LayoutSwitcherService},
        loadChildren: () => import('./features/remind-password/remind-password.module').then(m => m.RemindPasswordModule),
    },
    {path: AppUrls.SIGN_OUT, component: SignOutComponent},
    {path: 'sign-out', component: SignOutComponent},
    {path: 'logout', component: SignOutComponent},
    {
        path: AppUrls.DEFAULT_MAIN_PAGE,
        component: DefaultMainPageComponent,
    },
    {
        path: AppUrls.ONBOARDING,
        canActivate: [AuthGuard],
        resolve: {
            uaSettings: UaSettingsService,
        },
        loadChildren: () => import('./features/onboarding/onboarding.module').then(m => m.OnboardingModule),
    },
    {
        path: 'notifications/preview',
        canActivate: [AuthGuard],
        component: PreviewPageComponent,
    },
    {
        data: <PermissionsGuardData>{permission: 'canViewNewNavigationMenu'},
        canActivate: [PermissionsGuard],
        path: 'v3',
        loadChildren: () => import('./features/iframe-pages/iframe-pages.module').then(m => m.IframePagesModule),
    },
    {
        path: '',
        canActivate: [AuthGuard, OnboardingGuard],
        resolve: {
            uaSettings: UaSettingsService,
            appNewRelease: AppNewReleaseResolver,
        },
        children: [
            {
                path: '',
                redirectTo: AppUrls.BUSINESS_OVERVIEW,
                pathMatch: 'full',
            },
            {
                data: <PermissionsGuardData>{permission: 'canViewDashboard'},
                canActivate: [PermissionsGuard],
                path: AppUrls.ANALYTICS,
                loadChildren: () => import('./features/analytics/analytics.module').then(m => m.AnalyticsModule),
            },
            {
                data: <PermissionsGuardData>{
                    permission: 'canViewDashboard',
                    redirectToHome: true,
                },
                canActivate: [PermissionsGuard],
                path: AppUrls.DASHBOARD,
                loadChildren: () => import('./features/old-dashboard/old-dashboard.module').then(m => m.OldDashboardModule),
            },
            {
                data: <PermissionsGuardData>{permission: 'canViewApps', redirectToHome: true, breadcrumb: 'navigation.apps'},
                canActivate: [PermissionsGuard],
                path: AppUrls.APPS,
                loadChildren: () => import('./features/apps/apps.module').then(m => m.AppsModule),
            },
            {
                data: <PermissionsGuardData>{permission: 'canViewPayouts', redirectToHome: true},
                canActivate: [PermissionsGuard],
                path: AppUrls.PAYOUTS,
                loadChildren: () => import('./features/payouts/payouts.module').then(m => m.PayoutsModule),
            },
            {
                data: <PermissionsGuardData>{permission: 'canViewEmbeddedBidonUi'},
                canActivate: [PermissionsGuard],
                path: AppUrls.BIDON,
                loadChildren: () => import('./features/bidon/bidon.module').then(m => m.BidonModule),
            },
            {
                data: <PermissionsGuardData>{permission: 'canViewBidmanager'},
                canActivate: [PermissionsGuard],
                path: AppUrls.BID_MANAGER,
                loadChildren: () => import('./features/bid-manager/bid-manager.module').then(m => m.BidManagerModule),
            },
            {
                path: AppUrls.CREATIVE_LIBRARY,
                loadChildren: () => import('./features/creative-library/creative-library.module').then(m => m.CreativeLibraryModule),
            },
            {
                path: AppUrls.INTEGRATIONS,
                loadChildren: () => import('./features/integrations/integrations.module').then(m => m.IntegrationsModule),
            },
            {
                data: <PermissionsGuardData>{permission: 'canViewExperiments'},
                canActivate: [PermissionsGuard],
                path: AppUrls.EXPERIMENTS,
                loadChildren: () => import('./features/experiments/experiments.module').then(m => m.ExperimentsModule),
            },
        ],
    },
    {
        path: AppUrls.USER_PROFILE,
        canActivate: [AuthGuard],
        loadChildren: () => import('./features/profile/profile.module').then(m => m.ProfileModule),
    },
    {
        path: AppUrls.MEDIATIONS,
        data: {breadcrumb: 'navigation.mediation'},
        loadChildren: () => import('./features/mediations/mediations.module').then(m => m.MediationsModule),
    },
    {
        path: 'check-internet-connection',
        component: CheckInternetConnectionComponent,
    },
    {
        path: '500',
        component: InternalServerErrorComponent,
    },
    {
        path: '404',
        component: PageNotFoundComponent,
    },
    {
        path: '**',
        component: PageNotFoundComponent,
        canActivate: [LeavingPageGuardService],
    },
];
